"use client";

import { createUserWithEmailAndPassword, onAuthStateChanged } from "firebase/auth";
import { auth, db } from "@/app/lib/firebase";
import { doc, setDoc, Timestamp } from "firebase/firestore";
import { useRouter } from "next/navigation";
import { useState } from "react";
import Link from "next/link";
import SearchableSelect from "../components/SearchableSelect";
import { CITIES_BIH } from "../lib/cities";
import { trackEvent } from "../lib/analytics";
import { mixpanelTrack } from "../lib/mixpanel";

// Email validation function
function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

// Password strength validation
function isStrongPassword(password: string): boolean {
  // At least 6 characters
  return password.length >= 6;
}

export default function RegisterPage() {
  const router = useRouter();
  const [firstName, setFirstName] = useState("");
  const [surname, setSurname] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [city, setCity] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const validateForm = (): string | null => {
    if (!firstName.trim()) {
      return "Ime je obavezno.";
    }
    if (!surname.trim()) {
      return "Prezime je obavezno.";
    }
    if (!username.trim()) {
      return "Korisničko ime je obavezno.";
    }
    if (username.trim().length < 3) {
      return "Korisničko ime mora imati najmanje 3 karaktera.";
    }
    if (!email.trim()) {
      return "Email je obavezan.";
    }
    if (!isValidEmail(email.trim())) {
      return "Unesite validnu email adresu.";
    }
    if (!pass) {
      return "Lozinka je obavezna.";
    }
    if (!isStrongPassword(pass)) {
      return "Lozinka mora imati najmanje 6 karaktera.";
    }
    if (pass !== confirmPass) {
      return "Lozinke se ne poklapaju.";
    }
    if (!city.trim()) {
      return "Grad je obavezan.";
    }
    return null;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);

    const validationError = validateForm();
    if (validationError) {
      setErr(validationError);
      return;
    }

    setLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email.trim(),
        pass
      );

      const userId = userCredential.user.uid;

      // Sačekaj da se auth token propagira u Firestore (potrebno za security rules)
      await new Promise<void>((resolve) => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
          if (user && user.uid === userId) {
            unsubscribe();
            resolve();
          }
        });
        
        // Fallback timeout - ako auth state ne update-uje brzo, nastavi
        setTimeout(() => {
          unsubscribe();
          resolve();
        }, 500);
      });

      // Kreiraj/sačuvaj profil direktno u Firestore (bez dodatnih čitanja)
      const memberSince = Timestamp.fromMillis(Date.now());
      const profileRef = doc(db, "profiles", userId);
      const displayName =
        `${firstName.trim()} ${surname.trim()}`.trim() ||
        username.trim() ||
        "Korisnik";

      await setDoc(
        profileRef,
        {
          id: userId,
          name: firstName.trim(),
          surname: surname.trim(),
          username: username.trim(),
          city: city.trim() || undefined,
          displayName,
          memberSince,
          email: email.trim(), // Spremi email u profil za email obavještenja
        },
        { merge: true }
      );

      // GA4/Google Ads: prijava korisnika (sign_up) preko GTM dataLayer-a
      trackEvent("sign_up", {
        method: "email",
        user_id: userId,
        full_name: displayName,
        email: email.trim(),
      });

      // Mixpanel: Sign Up event
      mixpanelTrack("Sign Up", {
        user_id: userId,
        email: email.trim(),
        signup_method: "email",
      });

      // Kratko kašnjenje da GTM stigne obraditi event prije redirecta
      await new Promise((r) => setTimeout(r, 400));

      // Nakon registracije: ako korisnik dolazi iz "ormar" mini-quiz flowa,
      // odmah ga vrati na /ormar (da bude prijavljen i da otključa rezultat).
      const from =
        typeof window !== "undefined"
          ? new URLSearchParams(window.location.search).get("from")
          : null;
      window.location.href =
        from === "closet_quiz" ? "/ormar" : "/";
    } catch (e: any) {
      let errorMessage = "Greška pri registraciji.";
      
      // Check error code (Firebase errors have code property)
      const errorCode = e?.code || e?.error?.code;
      
      // Handle specific Firebase Auth errors with user-friendly messages
      if (errorCode === "auth/email-already-in-use") {
        errorMessage = "Email adresa je već u upotrebi. Molimo prijavite se ili koristite drugu email adresu.";
      } else if (errorCode === "auth/invalid-email") {
        errorMessage = "Nevalidna email adresa. Molimo unesite validnu email adresu.";
      } else if (errorCode === "auth/weak-password") {
        errorMessage = "Lozinka je previše slaba. Lozinka mora imati najmanje 6 karaktera.";
      } else if (errorCode === "auth/operation-not-allowed") {
        errorMessage = "Registracija nije dozvoljena. Kontaktirajte administratora.";
      } else if (errorCode === "auth/network-request-failed") {
        errorMessage = "Problem sa internet konekcijom. Provjerite svoju konekciju i pokušajte ponovo.";
      } else if (errorCode === "permission-denied" || errorCode === "missing-or-insufficient-permissions") {
        errorMessage = "Nemate dozvolu za kreiranje profila. Molimo kontaktirajte administratora.";
      } else if (e?.message) {
        // Check if message contains error code info
        const messageStr = String(e.message).toLowerCase();
        if (messageStr.includes("email-already-in-use") || messageStr.includes("email already in use")) {
          errorMessage = "Email adresa je već u upotrebi. Molimo prijavite se ili koristite drugu email adresu.";
        } else if (messageStr.includes("invalid-email") || messageStr.includes("invalid email")) {
          errorMessage = "Nevalidna email adresa. Molimo unesite validnu email adresu.";
        } else if (messageStr.includes("weak-password") || messageStr.includes("weak password")) {
          errorMessage = "Lozinka je previše slaba. Lozinka mora imati najmanje 6 karaktera.";
        } else {
          // Don't show raw Firebase error messages to users
          errorMessage = "Greška pri registraciji. Molimo provjerite podatke i pokušajte ponovo.";
        }
      }
      
      // Log error for debugging (but don't show Firebase error to user)
      // Only log if it's not a common user error (email-already-in-use is expected)
      if (errorCode !== "auth/email-already-in-use") {
        const errorDetails: any = {};
        let hasMeaningfulInfo = false;
        
        if (errorCode && typeof errorCode === "string" && errorCode.length > 0 && errorCode.trim().length > 0) {
          errorDetails.code = errorCode;
          hasMeaningfulInfo = true;
        }
        
        const errorMsg = e?.message || e?.error?.message;
        if (errorMsg && 
            typeof errorMsg === "string" && 
            errorMsg.length > 0 && 
            errorMsg.trim().length > 0 &&
            errorMsg !== "[object Object]" &&
            errorMsg !== "{}" &&
            !errorMsg.startsWith("{}")) {
          errorDetails.message = errorMsg;
          hasMeaningfulInfo = true;
        }
        
        if (hasMeaningfulInfo && Object.keys(errorDetails).length > 0) {
          console.warn("Registration error (logged for debugging):", errorDetails);
        }
      }
      
      // Set user-friendly error message
      setErr(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center px-3 sm:px-4 md:px-6 py-6 sm:py-8 md:py-12 bg-[var(--bg)]">
      <div className="w-full max-w-lg">
        <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 shadow-sm">
          <div className="text-center mb-4 sm:mb-6 md:mb-8">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[var(--accent)] mb-1 sm:mb-2">
              Registracija
            </h1>
            <p className="text-[var(--muted)] text-xs sm:text-sm">
              Kreiraj svoj račun i počni koristiti NOSIVA
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4" autoComplete="on">
            {/* Ime i Prezime u jednom redu */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2 text-[var(--text)]">
                  Ime <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full px-4 py-3 border border-[var(--border)] bg-[var(--card)] rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/20 focus:border-[var(--accent)] transition text-[var(--text)] placeholder:text-[var(--muted)]"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  type="text"
                  placeholder="Ime"
                  name="given-name"
                  autoComplete="given-name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-[var(--text)]">
                  Prezime <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full px-4 py-3 border border-[var(--border)] bg-[var(--card)] rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/20 focus:border-[var(--accent)] transition text-[var(--text)] placeholder:text-[var(--muted)]"
                  value={surname}
                  onChange={(e) => setSurname(e.target.value)}
                  type="text"
                  placeholder="Prezime"
                  name="family-name"
                  autoComplete="family-name"
                  required
                />
              </div>
            </div>

            {/* Korisničko ime */}
            <div>
              <label className="block text-xs sm:text-sm font-semibold mb-1.5 sm:mb-2 text-[var(--text)]">
                Korisničko ime <span className="text-red-500">*</span>
              </label>
              <input
                className="w-full px-2.5 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 border border-[var(--border)] bg-[var(--card)] rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/20 focus:border-[var(--accent)] transition text-xs sm:text-sm md:text-base text-[var(--text)] placeholder:text-[var(--muted)]"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                }}
                type="text"
                placeholder="korisnicko_ime"
                name="new-username"
                autoComplete="off"
                required
                minLength={3}
              />
              <p className="text-[10px] sm:text-xs text-[var(--muted)] mt-0.5 sm:mt-1">
                Minimum 3 karaktera, bez razmaka (npr. nadira, nadira_s)
              </p>
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs sm:text-sm font-semibold mb-1.5 sm:mb-2 text-[var(--text)]">
                Email adresa <span className="text-red-500">*</span>
              </label>
              <input
                className="w-full px-2.5 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 border border-[var(--border)] bg-[var(--card)] rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/20 focus:border-[var(--accent)] transition text-xs sm:text-sm md:text-base text-[var(--text)] placeholder:text-[var(--muted)]"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                placeholder="ime@primjer.com"
                name="email"
                autoComplete="email"
                autoFocus
                required
              />
              {email && !isValidEmail(email) && (
                <p className="text-[10px] sm:text-xs text-red-500 mt-0.5 sm:mt-1">
                  Unesite validnu email adresu
                </p>
              )}
            </div>

            {/* Grad */}
            <div>
              <label className="block text-xs sm:text-sm font-semibold mb-1.5 sm:mb-2 text-[var(--text)]">
                Grad <span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                value={city}
                onChange={(value) => setCity(value)}
                options={CITIES_BIH}
                placeholder="Odaberi grad"
                allowCustom={false}
              />
            </div>

            {/* Lozinka */}
            <div>
              <label className="block text-xs sm:text-sm font-semibold mb-1.5 sm:mb-2 text-[var(--text)]">
                Lozinka <span className="text-red-500">*</span>
              </label>
              <input
                className="w-full px-2.5 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 border border-[var(--border)] bg-[var(--card)] rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/20 focus:border-[var(--accent)] transition text-xs sm:text-sm md:text-base text-[var(--text)] placeholder:text-[var(--muted)]"
                value={pass}
                onChange={(e) => setPass(e.target.value)}
                type="password"
                placeholder="Minimum 6 karaktera"
                name="new-password"
                autoComplete="new-password"
                required
                minLength={6}
              />
              {pass && !isStrongPassword(pass) && (
                <p className="text-[10px] sm:text-xs text-red-500 mt-0.5 sm:mt-1">
                  Lozinka mora imati najmanje 6 karaktera
                </p>
              )}
            </div>

            {/* Potvrda lozinke */}
            <div>
              <label className="block text-xs sm:text-sm font-semibold mb-1.5 sm:mb-2 text-[var(--text)]">
                Potvrdi lozinku <span className="text-red-500">*</span>
              </label>
              <input
                className="w-full px-2.5 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 border border-[var(--border)] bg-[var(--card)] rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/20 focus:border-[var(--accent)] transition text-xs sm:text-sm md:text-base text-[var(--text)] placeholder:text-[var(--muted)]"
                value={confirmPass}
                onChange={(e) => setConfirmPass(e.target.value)}
                type="password"
                placeholder="Ponovi lozinku"
                name="new-password-confirm"
                autoComplete="new-password"
                required
              />
              {confirmPass && pass !== confirmPass && (
                <p className="text-[10px] sm:text-xs text-red-500 mt-0.5 sm:mt-1">
                  Lozinke se ne poklapaju
                </p>
              )}
            </div>

            {err && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400 px-3 sm:px-4 py-2 sm:py-2.5 md:py-3 rounded-lg sm:rounded-xl text-xs sm:text-sm">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 sm:py-3 md:py-4 rounded-lg sm:rounded-xl bg-[var(--accent)] text-white text-xs sm:text-sm md:text-base font-bold hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed transition shadow-sm hover:shadow-md"
            >
              {loading ? "Registracija u toku…" : "Kreiraj račun"}
            </button>

            <p className="text-xs sm:text-sm text-center text-[var(--muted)] mt-4 sm:mt-5 md:mt-6">
              Već imaš nalog?{" "}
              <Link
                className="font-semibold text-[var(--accent)] hover:underline"
                href="/login"
              >
                Prijavi se
              </Link>
            </p>
          </form>
        </div>
      </div>
    </main>
  );
}
